import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useVoiceRecognizer from "../../../../hooks/useVoiceRecognizer";
import { uploadFile, SimpleUploadConfig } from "../../../../utils/uploadHelper";
import { generateVoiceFilePath } from "../../../../utils/pathGenerator";
import {
  uploadFileWithRetry,
  globalUploadManager,
} from "../../../../utils/uploadRetry";
import InputModeButton from "./InputModeButton";
import CircleButton from "./CircleButton";
import { sleep } from '@/utils/toolUtils';
import { Toast } from "antd-mobile";
import { getCorpIdFromToken } from "../../../../lib/auth";
import { useI18n } from '@/hooks/useI18n';
import { useUserStore, useSettingData } from '@/stores/userStore';
import { Converter } from 'opencc-js';
import { LanguageCode } from '../../../../constants/languages';

// 声明全局 Recorder 类型
declare global {
  interface Window {
    Recorder: any;
  }
}

interface VoiceInputProps {
  onSendMessage?: (
      message: string,
      type: "text" | "voice",
      audioBlob?: Blob,
      fileUrl?: string,
      duration?: number
  ) => void;
  isStartRecording?: () => void;
  disabled?: boolean;
  placeholder?: string;
  maxRecordingTime?: number; // 录音最大时长（秒），默认120秒
  enableUpload?: boolean; // 是否启用文件上传功能，默认true
  uploadConfig?: SimpleUploadConfig; // 文件上传配置
  sceneLang?: string; // 场景语言
  sessionId?: string; // 会话ID，用于记录使用情况
}

const VoiceInput: React.FC<VoiceInputProps> = ({
  onSendMessage,
  isStartRecording,
  disabled = false,
  placeholder,
  maxRecordingTime = 120, // 默认120秒
  enableUpload = true, // 默认启用文件上传
  uploadConfig = { fileType: "voice" }, // 默认语音文件上传配置
  sceneLang = LanguageCode.ZH_CN, // 默认中文
  sessionId, // 会话ID
}) => {
  const { t } = useI18n();
  const defaultPlaceholder = placeholder || t('ai.training.inputPlaceholder');
  const [inputMode, setInputMode] = useState<"voice" | "text">("voice");
  const [textInput, setTextInput] = useState("");
  const [recognizedText, setRecognizedText] = useState("");
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recorderInstance, setRecorderInstance] = useState<any>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isSending, setIsSending] = useState(false);
  // 根据场景语言自动判断是否需要简繁转换：当语言为 tw 时启用
  const [shouldConvertToTraditional, setShouldConvertToTraditional] = useState(
    sceneLang === LanguageCode.ZH_TW
  );
  let tempBlob :any
  
  // 初始化简繁转换器
  const converter = useRef<any>(null);
  useEffect(() => {
    // 使用 opencc-js 创建简体到繁体的转换器
    converter.current = Converter({ from: 'cn', to: 'tw' });
  }, []);
  
  // 监听 sceneLang 变化，自动更新简繁转换开关
  useEffect(() => {
    setShouldConvertToTraditional(sceneLang === LanguageCode.ZH_TW);
  }, [sceneLang]);
  // 语音识别相关
  const [audioLevel, setAudioLevel] = useState(0);
  const {
    isRecording,
    isConnecting,
    recognizedText: voiceText,
    audioInfo,
    startRecording,
    stopRecording,
  } = useVoiceRecognizer({
    onTextRecognized: (text: string) => {
      setRecognizedText(text);
    },
    onAudioLevel: (level: number) => {
      setAudioLevel(level);
    },
    language: sceneLang, // 传递场景语言
    sceneId: sessionId, // 传递会话ID作为场景ID，用于记录使用情况
  });

  const settingData = useSettingData();

  // console.log('settingData', settingData);

  /**
   * 简体中文转繁体中文
   * @param text 待转换的文本
   * @returns 转换后的文本（如果启用转换且是中文则返回繁体，否则返回原文）
   */
  const convertToTraditionalIfNeeded = (text: string): string => {
    if (!shouldConvertToTraditional || !text || !converter.current) {
      return text;
    }

    // 检测文本中是否包含中文字符
    const hasChinese = /[\u4e00-\u9fa5]/.test(text);
    
    if (hasChinese) {
      try {
        return converter.current(text);
      } catch (error) {
        console.error('简繁转换失败:', error);
        return text;
      }
    }
    
    return text;
  };

  // 初始化Recorder.js
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 50; // 最多重试5秒

    const initRecorder = () => {
      if (window.Recorder) {
        console.log("Recorder.js已加载，开始初始化");
        const newRecorder = window.Recorder({
          type: "mp3",
          sampleRate: 16000,
          bitRate: 16,
        });

        newRecorder.open(
            () => {
              console.log("Recorder初始化成功");
              setRecorderInstance(newRecorder);
            },
            (msg: string) => {
              console.error("Recorder初始化失败:", msg);
            }
        );
      } else {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initRecorder, 100);
        } else {
          console.error("Recorder.js加载超时，请检查网络连接");
        }
      }
    };

    // 只在组件首次挂载时初始化
    if (!recorderInstance) {
      initRecorder();
    }

    return () => {
      if (recorderInstance) {
        recorderInstance.close();
      }
    };
  }, []); // 移除依赖项，只在组件挂载时执行一次

  const handleSend = async () => {
    // 防止重复点击
    if (isSending) {
      return;
    }

    if (isConnecting) {
      Toast.show({
        content: t('ai.chat.connectingNetwork', '正在连接网络，请稍后...'),
        duration: 1000,
      })
      return;
    }
    if (!recognizedText.trim()&&inputMode === "voice") {
      Toast.show({
        content: t('ai.chat.noContentRecognized', '没有识别到内容，请在安静环境说话'),
        duration: 1000,
      })
      return;
    }

    // 设置发送状态，防止重复点击
    setIsSending(true);

    try {

      if (inputMode === "text" && textInput.trim() && onSendMessage) {
        // 文本模式：根据 flag 决定是否转换简体为繁体
        const finalText = convertToTraditionalIfNeeded(textInput.trim());
        onSendMessage(finalText, "text", undefined, undefined, undefined);
        setTextInput("");
      } else if (inputMode === "voice" && onSendMessage) {
        // 预生成文件路径
        let fileUrl: string | undefined;
        const customPath = generateVoiceFilePath();
        const fileName =
            customPath.split("/").pop() || `recording_${Date.now()}.mp3`;

        // 从JWT token中获取corp_id，如果不存在则使用默认值
        const corpId = getCorpIdFromToken() || 'default';

        // 构造预期的文件URL（基于路径，包含corp_id）
        fileUrl = `${corpId}/training_scenario/${customPath}`;
        // 检查是否有识别到的文本
        if (recognizedText.trim()) {
          // 语音模式：根据 flag 决定是否转换简体为繁体
          const finalText = convertToTraditionalIfNeeded(recognizedText.trim());
          // 立即发送消息，使用预生成的文件URL
          onSendMessage(
              finalText,
              "voice",
              audioBlob || undefined,
              `${settingData?.CDN_BASE_URL}${fileUrl}`,
              recordingTime
          );
          setTimeout(() => {
            setRecognizedText("");
            setAudioBlob(null);
          }, 1000);
        }
        // 如果正在录音，先停止录音
        if (isRecording) {
          handleStopRecording();
          await sleep(3500); // 延迟1.5秒
          console.log("enableUpload && tempBlob", enableUpload, tempBlob);

          // 如果启用上传且有录音文件，预生成文件路径并在后台异步上传
          if (enableUpload && tempBlob) {
            // 后台异步上传，不阻塞发送流程
            (async () => {
              const uploadId = `voice_${Date.now()}_${Math.random()
                  .toString(36)
                  .substring(2, 8)}`;

              try {
                // 创建文件对象
                const audioFile = new File([tempBlob], fileName, {
                  type: "audio/mp3",
                });

                // 开始上传状态跟踪
                globalUploadManager.startUpload(uploadId);

                // 使用带重试的上传功能
                const uploadResult = await uploadFileWithRetry(
                    audioFile,
                    {
                      ...uploadConfig,
                      businessContext: {
                        ...uploadConfig.businessContext,
                        custom_path: fileUrl,
                      },
                    },
                    { maxRetries: 3, retryDelay: 1000, backoffMultiplier: 2 }, // 重试配置
                    (progress) => {
                      // 更新上传进度
                      globalUploadManager.updateProgress(uploadId, progress);
                    },
                    (attempt, error) => {
                      // 重试回调
                      console.warn(`录音文件上传重试第${attempt}次，错误:`, error);
                    }
                );

                if (uploadResult.success) {
                  console.log(
                      "录音文件上传成功，预定义路径:",
                      fileUrl,
                      "实际URL:",
                      uploadResult.fileUrl
                  );
                  globalUploadManager.uploadSuccess(uploadId, uploadResult);
                } else {
                  console.error("录音文件上传最终失败:", uploadResult.error);
                  globalUploadManager.uploadFailed(
                      uploadId,
                      uploadResult.error || "上传失败"
                  );
                }
              } catch (error) {
                const errorMsg =
                    error instanceof Error ? error.message : "上传异常";
                console.error("上传录音文件时出错:", errorMsg);
                globalUploadManager.uploadFailed(uploadId, errorMsg);
              } finally {
                // 清理上传状态（延迟清理，给UI时间显示结果）
                setTimeout(() => {
                  globalUploadManager.cleanup(uploadId);
                }, 5000);
              }
            })();
          }
        }
      }
    } finally {
      // 1秒后重置发送状态
      setTimeout(() => {
        setIsSending(false);
      }, 1000);
    }
  };

  const toggleInputMode = () => {
    const newMode = inputMode === "voice" ? "text" : "voice";
    setInputMode(newMode);

    if (newMode === "text" && isRecording) {
      handleStopRecording();
    }
  };

  const cancelRecording = () => {
    if (isRecording) {
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      handleStopRecording();
      setRecognizedText("");
      setAudioBlob(null);
      setRecordingTime(0);
    }
  };

  const handleStartRecording = async () => {
    isStartRecording && isStartRecording();
    if (disabled) return;

    try {
      // 同时启动语音识别和录音保存
      await startRecording();

      if (recorderInstance) {
        recorderInstance.start();
      }

      // 开始计时
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          // 达到最大录音时长时自动停止
          if (newTime >= maxRecordingTime) {
            handleStopRecording();
            return maxRecordingTime;
          }
          return newTime;
        });
      }, 1000);
    } catch (error) {
      console.error("启动录音失败:", error);
    }
  };

  const handleStopRecording = async () => {

    try {
      // 清除计时器
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }

      // 停止语音识别
      await stopRecording();
      console.log('recorderInstance====>',recorderInstance);

      // 停止录音保存
      if (recorderInstance) {
        recorderInstance.stop((blob: Blob) => {
          console.log('blob===>', blob);

          setAudioBlob(blob);
          tempBlob = blob

        });
      }
    } catch (error) {
      console.error("停止录音失败:", error);
    }
  };

  // 判断是否显示发送按钮
  const shouldShowSendButton = () => {
    if (inputMode === "text") {
      return textInput.trim().length > 0;
    } else {
      return isRecording || recognizedText.trim().length > 0;
    }
  };

  // 判断是否禁用发送按钮
  const isSendDisabled = () => {
    return disabled || isSending;
  };

  return (
      <motion.div
          className='w-full max-w-4xl mx-auto'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* 底部输入区域 */}
        <motion.div
            className='flex items-center p-4 bg-white rounded-lg border border-gray-200 shadow-sm'
            animate={{
              gap: shouldShowSendButton() ? "12px" : "0px",
            }}
            whileHover={{
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
              borderColor: "rgb(59, 130, 246)",
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          {/* 1. 切换按钮 */}
          <motion.div
              className='mr-2'
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <InputModeButton
                inputMode={inputMode}
                isRecording={isRecording}
                disabled={disabled}
                onToggleMode={toggleInputMode}
                onCancelRecording={cancelRecording}
            />
          </motion.div>

          {/* 2. 中间区域 */}
          <motion.div
              className='bg-gradient-to-r from-[#4E8FFD] to-[#A785F5] rounded-3xl overflow-hidden flex-1'
              animate={{
                background:
                    inputMode === "text"
                        ? "linear-gradient(90deg, #f3f4f6, #e5e7eb)"
                        : isRecording
                            ? "linear-gradient(90deg, #ef4444, #dc2626)"
                            : "linear-gradient(90deg, #4E8FFD, #A785F5)",
                marginRight: shouldShowSendButton() ? "12px" : "0px",
              }}
              layout
              transition={{
                layout: {
                  duration: 0.5,
                  ease: "easeInOut",
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                },
                background: {
                  duration: 0.4,
                  ease: "easeInOut",
                },
              }}
          >
            <AnimatePresence mode='wait'>
              <motion.div
                  key={inputMode}
                  initial={{ opacity: 0, x: inputMode === "text" ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: inputMode === "text" ? 20 : -20 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {inputMode === "text" ? (
                    /* 文字输入框 */
                    <motion.input
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder={defaultPlaceholder}
                        disabled={disabled}
                        className='w-full px-6 py-2 bg-transparent border-none rounded-lg resize-none min-h-[22px] max-h-[120px] outline-none text-gray-700 placeholder-gray-500 transition-all duration-200'
                        whileFocus={{
                          scale: 1.01
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                    />
                ) : (
                    /* 语音状态展示区域 */
                    <motion.div
                        onClick={
                          // isRecording ? handleStopRecording : handleStartRecording
                          isRecording ? cancelRecording : handleStartRecording
                        }
                        className='flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer'
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      <motion.div
                          key={
                            isRecording
                                ? "recording"
                                : recognizedText
                                    ? "recognized"
                                    : "idle"
                          }
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className='flex items-center gap-3 w-full'
                      >
                        {isRecording ? (
                            <>
                              <div className='flex items-center gap-2'>
                                <motion.div
                                    className='w-3 h-3 bg-white rounded-full'
                                    animate={{
                                      scale: [1, 1.2, 1],
                                      opacity: [1, 0.7, 1],
                                    }}
                                    transition={{
                                      duration: 1.5,
                                      repeat: Infinity,
                                      ease: "easeInOut",
                                    }}
                                ></motion.div>
                                <motion.span
                                    className='text-sm text-white/80'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                >
                                  {Math.floor(recordingTime / 60)}:
                                  {(recordingTime % 60).toString().padStart(2, "0")}/
                                  {Math.floor(maxRecordingTime / 60)}:
                                  {(maxRecordingTime % 60)
                                      .toString()
                                      .padStart(2, "0")}
                                </motion.span>
                              </div>
                              {/* 音频电平显示 */}
                              <div className='flex-1 flex items-center gap-1'>
                                {Array.from({ length: 10 }).map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className='w-1 bg-white rounded-full'
                                        animate={{
                                          height: audioLevel > (i + 1) * 10 ? 16 : 4,
                                          opacity: audioLevel > (i + 1) * 10 ? 1 : 0.5,
                                        }}
                                        transition={{
                                          duration: 0.1,
                                          ease: "easeOut",
                                        }}
                                    />
                                ))}
                              </div>
                            </>
                        ) : recognizedText ? (
                            <motion.div
                                className='flex-1'
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                            >
                        <span className='text-white font-medium whitespace-nowrap opacity-0'>
                          {recognizedText}
                          {/* 这里隐藏掉识别的文字 */}
                        </span>
                            </motion.div>
                        ) : (
                            <motion.div
                                className='flex items-center gap-2 text-white'
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                            >
                              <motion.span
                                  className='text-sm'
                                  animate={{
                                    opacity: [0.7, 1, 0.7],
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  }}
                              >
                                {t('ai.training.clickToRecord')}
                              </motion.span>
                            </motion.div>
                        )}
                      </motion.div>
                    </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>

          {/* 3. 发送按钮 */}
          <AnimatePresence mode='wait'>
            {shouldShowSendButton() && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.8, x: 20, width: 0 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      x: 0,
                      width: "auto",
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      x: 20,
                      width: 0,
                    }}
                    transition={{
                      duration: 0.4,
                      ease: "easeInOut",
                      type: "spring",
                      stiffness: 300,
                      damping: 25,
                    }}
                    layout
                >
                  <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                  >
                    <CircleButton
                        onClick={handleSend}
                        disabled={isSendDisabled()}
                        title={t('ai.training.sendMessage')}
                        textColor='text-white'
                        className='flex-shrink-0 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <motion.div
                          key={"send"}
                          initial={{ opacity: 0, rotate: -90 }}
                          animate={{ opacity: 1, rotate: 0 }}
                          exit={{ opacity: 0, rotate: 90 }}
                          transition={{ duration: 0.2 }}
                      >
                        <svg
                            className='w-4 h-4'
                            fill='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path d='M2.01 21L23 12 2.01 3 2 10l15 2-15 2z' />
                        </svg>
                      </motion.div>
                    </CircleButton>
                  </motion.div>
                </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
  );
};

export default VoiceInput;
